import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { SelectPrograma } from "./SelectPrograma";
import { useCargaMasivaAprendicesProgramas } from "@/hooks/aprendicesProgramas/useAprendicesProgramas";
import {
  uploadExcelSchema,
  type UploadExcelInput,
} from "@/lib/validaciones/aprendicesProgramas.validacion";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Download, Info } from "lucide-react";
import { toast } from "sonner";

export function UploadExcelForm({
  setOpen,
}: {
  setOpen: (open: boolean) => void;
}) {
  const form = useForm<UploadExcelInput>({
    resolver: zodResolver(uploadExcelSchema),
    defaultValues: {
      idPrograma: 0,
      archivo: undefined,
    },
  });

  const { mutate, isPending } = useCargaMasivaAprendicesProgramas();

  const onSubmit = (data: UploadExcelInput) => {
    if (!data.archivo) return;
    mutate(
      {
        idPrograma: data.idPrograma,
        archivo: data.archivo,
      },
      {
        onSuccess: () => {
          form.reset();
          setOpen(false);
        },
      }
    );
  };

  const handleDownloadTemplate = () => {
    const csvContent = `nombreUsuario,nombre,apellidoPaterno,apellidoMaterno,correo
jperez,Juan,Pérez,García,juan.perez@example.com
mrodriguez,María,Rodríguez,López,maria.rodriguez@example.com
cgarcia,Carlos,García,Martínez,carlos.garcia@example.com`;

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute("download", "plantilla_usuarios.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success("Plantilla descargada exitosamente");
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        {/* Template Download Alert */}
        <Alert className="bg-blue-50 border-blue-200">
          <Info className="h-4 w-4 text-blue-600" />
          <AlertTitle className="text-blue-900">Formato de archivo</AlertTitle>
          <AlertDescription className="text-blue-800 space-y-2">
            <p className="text-sm">
              El archivo Excel debe contener las siguientes columnas:
              <br />
              <strong>
                nombreUsuario, nombre, apellidoPaterno, apellidoMaterno, correo
              </strong>
            </p>
            <p className="text-xs text-blue-700">
              ⚠️ <strong>Importante:</strong> Ya NO es necesario incluir la
              columna "contraseña". Los usuarios recibirán un correo de
              activación para establecer su propia contraseña.
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleDownloadTemplate}
              className="mt-2 border-blue-300 text-blue-700 hover:bg-blue-100"
            >
              <Download className="w-4 h-4 mr-2" />
              Descargar plantilla de ejemplo
            </Button>
          </AlertDescription>
        </Alert>

        <FormField
          control={form.control}
          name="idPrograma"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Programa</FormLabel>
              <FormControl>
                <SelectPrograma
                  value={field.value ? String(field.value) : null}
                  placeholder="Selecciona un programa"
                  onChange={(option) => field.onChange(Number(option?.value))}
                  disabled={isPending}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="archivo"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Archivo Excel</FormLabel>
              <FormControl>
                <div className="flex flex-col gap-2">
                  <label
                    htmlFor="archivo-excel-upload"
                    className="cursor-pointer px-4 py-2 rounded bg-gray-100 border border-gray-300 text-gray-700 hover:bg-gray-200 transition flex items-center gap-2"
                  >
                    <svg
                      className="w-5 h-5 text-gray-500"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5-5m0 0l5 5m-5-5v12"
                      />
                    </svg>
                    <span>
                      {field.value
                        ? "Cambiar archivo"
                        : "Selecciona un archivo"}
                    </span>
                  </label>
                  <input
                    id="archivo-excel-upload"
                    type="file"
                    accept=".xlsx,.xls"
                    className="hidden"
                    disabled={isPending}
                    onChange={(e) => field.onChange(e.target.files?.[0])}
                  />
                  {field.value && (
                    <span className="text-xs text-gray-500 mt-1">
                      Archivo seleccionado: {field.value.name}
                    </span>
                  )}
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button type="submit" disabled={isPending} className="w-full">
          {isPending ? "Subiendo..." : "Subir Excel"}
        </Button>
      </form>
    </Form>
  );
}
